import { Box, Flex, Radio, Text, Title } from '@mantine/core'
import clsx from 'clsx'
import Image from 'next/image'
import { useEffect, useState } from 'react'

import { useTranslation } from '@flecto/translations'
import { type CoreUserIdentity } from '@flecto/types'
import { Badge, Modal } from '@flecto/ui'

import styles from './styles.module.css'
import { useGoogleCalendarActions } from './useGoogleCalendarActions'

export type ModalLinkGoogleCalendarProps = {
  identity: CoreUserIdentity
}

export function ModalLinkGoogleCalendar({
  identity,
}: ModalLinkGoogleCalendarProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'profile_account' })

  const [unifiedEvent, setUnifiedEvent] = useState(false)

  const { linkGoogleCalendar, isLoading } = useGoogleCalendarActions({
    identity,
    isUnifiedEvent: unifiedEvent,
  })

  useEffect(() => {
    if (typeof identity?.google_calendar_unified_event === 'boolean') {
      setUnifiedEvent(identity?.google_calendar_unified_event)
    }
  }, [identity])

  const handleConfirm = async () => {
    await linkGoogleCalendar()
  }

  return (
    <Modal size='lg'>
      <Modal.Title>
        {t('modal_link_google_calendar.title')}
        <Badge ml='xs'>{identity.email}</Badge>
      </Modal.Title>
      <Modal.Content>
        <Title order={5}>
          {t('modal_link_google_calendar.settings_title')}
        </Title>
        <Text c='dimmed'>
          {t('modal_link_google_calendar.settings_description')}
        </Text>
        <Flex mt='md' gap='lg' justify='space-between'>
          <Box
            className={clsx(styles.option, { [styles.active]: !unifiedEvent })}
            onClick={() => setUnifiedEvent(false)}
          >
            <Radio
              label={t('modal_link_google_calendar.option_separated')}
              checked={!unifiedEvent}
            />
            <Image
              src={require('./gcal-booking-split.png')}
              alt={t('modal_link_google_calendar.option_separated')}
            />
          </Box>
          <Box
            className={clsx(styles.option, { [styles.active]: unifiedEvent })}
            onClick={() => setUnifiedEvent(true)}
          >
            <Radio
              label={t('modal_link_google_calendar.option_full')}
              checked={unifiedEvent}
            />
            <Image
              src={require('./gcal-booking-full.png')}
              alt={t('modal_link_google_calendar.option_full')}
            />
          </Box>
        </Flex>
      </Modal.Content>
      <Modal.Cancel>{t('modal_link_google_calendar.btn_cancel')}</Modal.Cancel>
      <Modal.Submit onClick={() => handleConfirm()} loading={isLoading}>
        {t('modal_link_google_calendar.btn_submit')}
      </Modal.Submit>
    </Modal>
  )
}
